import { z } from 'zod'

export const MEDIA_ORIGINS = ['photo', 'ia-generee', 'ia-retouchee'] as const
export type MediaOrigin = (typeof MEDIA_ORIGINS)[number]

/**
 * L'origine d'un visuel, au sens de l'article 50 du règlement (UE) 2024/1689.
 *
 * `errorMap` plutôt qu'un message par cas : quelle que soit la façon dont la
 * valeur manque — absente du corps, chaîne vide de la première option du menu
 * déroulant, ou valeur inventée — la gérante doit lire la même chose, et c'est
 * la seule action qui la débloque. Surtout, ce schéma n'a **pas** de
 * `.default()` : une valeur par défaut classerait silencieusement en « photo »
 * un visuel qui aurait dû être signalé.
 */
export const mediaOriginSchema = z.enum(MEDIA_ORIGINS, {
  errorMap: () => ({ message: 'L’origine du visuel est obligatoire' }),
})

/** Pourcentage de la largeur ou de la hauteur de l'image, bornes comprises. */
const focalSchema = z.number().int().min(0).max(100)

/**
 * Ce que la gérante saisit sur un média, et ce qui reste modifiable après
 * coup : requalifier une origine ou corriger une description ne demande jamais
 * de re-téléverser le fichier.
 */
export const mediaMetadataSchema = z.object({
  alt: z
    .string({ required_error: 'La description est obligatoire' })
    .trim()
    .min(1, 'La description est obligatoire'),
  origin: mediaOriginSchema,
  // Le centre est le cadrage neutre : c'est ce que fait un `object-fit: cover`
  // sans instruction, donc le défaut ne change rien à ce qui s'affiche.
  focalX: focalSchema.default(50),
  focalY: focalSchema.default(50),
})
export type MediaMetadata = z.infer<typeof mediaMetadataSchema>

/**
 * Les quatre visuels de la page d'accueil. « Soins signature » est le seul
 * emplacement nullable : son absence masque la section sur le site, ce qui est
 * précisément l'état d'aujourd'hui. Vider les trois autres n'a aucun sens —
 * la page afficherait un trou — et l'API le refuse.
 */
/**
 * Ce qu'un média expose au site : de quoi composer un `srcset`, appliquer le
 * point focal et poser le signalement d'origine. Ni empreinte, ni nom de
 * fichier, ni dates — rien qui ne serve à l'affichage.
 */
export const publicMediaSchema = mediaMetadataSchema.extend({
  id: z.string().uuid(),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  widths: z.array(z.number().int().positive()).min(1),
})
export type PublicMedia = z.infer<typeof publicMediaSchema>

export const HOME_VISUAL_SLOTS = ['hero', 'jacuzzi', 'produits', 'soinsSignature'] as const
export type HomeVisualSlot = (typeof HOME_VISUAL_SLOTS)[number]

/**
 * Le nom de chaque emplacement, tel que la gérante le lit. Partagé plutôt que
 * recopié : l'API s'en sert pour dire quel emplacement retient une image
 * qu'on tente de supprimer, l'administration pour intituler ses champs, et
 * les deux doivent nommer la même chose de la même façon.
 */
export const HOME_VISUAL_LABELS: Record<HomeVisualSlot, string> = {
  hero: 'Visuel d’ouverture',
  jacuzzi: 'Bande — Jacuzzi & hammam',
  produits: 'Bande — Produits Baïja',
  soinsSignature: 'Bande — Soins signature',
}

export const homeVisualsSchema = z.object({
  hero: z.string().uuid(),
  jacuzzi: z.string().uuid(),
  produits: z.string().uuid(),
  soinsSignature: z.string().uuid().nullable(),
})
export type HomeVisuals = z.infer<typeof homeVisualsSchema>

/**
 * La même chose côté lecture, tous les emplacements nullables. Le schéma
 * d'écriture ci-dessus exige les trois emplacements occupés, mais il existe un
 * état antérieur légitime : celui d'une base pas encore amorcée, où aucun
 * visuel n'a jamais été placé. Le site sert alors ses photos de repli.
 */
export const storedHomeVisualsSchema = z.object({
  hero: z.string().uuid().nullable(),
  jacuzzi: z.string().uuid().nullable(),
  produits: z.string().uuid().nullable(),
  soinsSignature: z.string().uuid().nullable(),
})
export type StoredHomeVisuals = z.infer<typeof storedHomeVisualsSchema>

export const EMPTY_HOME_VISUALS: StoredHomeVisuals = {
  hero: null,
  jacuzzi: null,
  produits: null,
  soinsSignature: null,
}

/**
 * Ce que la route publique rend : chaque emplacement avec son média résolu, ou
 * `null`. Un `null` n'est pas une anomalie — c'est l'état de « Soins
 * signature » tant qu'aucune photo n'y est placée, et celui de tous les
 * emplacements sur une base pas encore amorcée.
 */
export type HomeVisualsContent = Record<HomeVisualSlot, PublicMedia | null>
